import { createInstance } from 'i18next';
import ICU from 'i18next-icu';
import I18nextBrowserLanguageDetector from 'i18next-browser-languagedetector';
import { serviceI18NResourcesToBackendPlugin } from 'virtual:@squareup/service-i18n/resources';
import { defaultI18nConfig } from './config';
/**
 * i18next instance to be used by component libraries.
 *
 * In development mode, the `squareI18n` plugin provides resources
 * dynamically by combining all `en.json` files from the project and its dependencies,
 * as part of the 'virtual:@squareup/service-i18n/resources' module.
 *
 * In production mode, the `squareI18n` dynamically imports
 * necessary locale files based on the current language.
 *
 * This instance should usually only be used via the withI18n HOC:
 *
 * ```ts
 * import { withI18n } from '@squareup/service-i18n';
 *
 * // Now this component can be safely published and used by external projects
 * const LocalizedComponent = withI18n(LocalizedComponent)
 * ```
 */
const i18next = createInstance();
i18next
    .use(ICU)
    .use(I18nextBrowserLanguageDetector)
    .use(serviceI18NResourcesToBackendPlugin());
export async function initializeLibraryI18n(options) {
    if (i18next.isInitialized) {
        return i18next;
    }
    await i18next.init({
        ...defaultI18nConfig,
        missingKeyHandler: options?.missingKeyHandler,
        saveMissing: !!options?.missingKeyHandler,
        ...options,
    });
    return i18next;
}
export const i18nLibraryInstance = i18next;
// This prevents a locale file change (en.json) from causing this module to be
// reloaded, which causes an HMR race condition where components `withI18n` will
// be updated before `initializeLibraryI18n` is ready causing an error. Vite
// plugin will take care of refreshing the app when locales change.
if (import.meta.hot) {
    import.meta.hot.accept();
}
